import { useReduxState } from 'hooks';
import { ApiDownloadButton, Button, ConfirmButton } from 'components/common';
import { Icon } from 'components/common/icons';

import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  customStatusText: string;
  defaultStatusText: string;
  downloadableFileUrl?: string;
  hasCustomValue: boolean;
  icon: IconProp;
  onCancel?: () => void;
  onRemove?: () => void;
  onReplace?: () => void;
  showCancel?: boolean;
  useDetachedStyles?: boolean;
}

const EditorBlockTranslationReplace = ({
  customStatusText,
  defaultStatusText,
  downloadableFileUrl,
  hasCustomValue,
  icon,
  onCancel,
  onRemove,
  onReplace,
  showCancel,
  useDetachedStyles
}: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <div
      className={`translation-replace ${
        useDetachedStyles ? 'translation-replace-detached' : ''
      }`}
    >
      {showCancel ? (
        <Button
          category="light"
          icon="arrow-left"
          onClick={onCancel}
          size="tiny"
          text={messages.commonCancel}
        />
      ) : (
        <>
          <div className="status">
            <div className="status-icon">
              <Icon icon={icon} />
            </div>

            <div className="status-label">
              {hasCustomValue ? customStatusText : defaultStatusText}
            </div>
          </div>

          <div className="actions">
            {hasCustomValue && onRemove ? (
              <ConfirmButton
                actionText={messages.commonRemove}
                buttonCategory="light"
                buttonIcon="trash-alt"
                buttonSize="tiny"
                buttonText={messages.commonRemove}
                confirmText={messages.courseLanguageVersionsRemoveFile}
                confirmTitle={messages.uploaderRemoveFile}
                onConfirm={() => {
                  onRemove();
                  return true;
                }}
              />
            ) : null}

            {onReplace ? (
              <Button
                category="light"
                icon="sync-alt"
                onClick={onReplace}
                size="tiny"
                text={messages.editorBlockImageReplace}
              />
            ) : null}

            {downloadableFileUrl ? (
              <ApiDownloadButton
                category="light"
                filename={downloadableFileUrl.split('/').pop()}
                icon="download"
                url={downloadableFileUrl}
                external={true}
                size="tiny"
                tooltip={messages.commonDownloadFile}
              />
            ) : null}
          </div>
        </>
      )}

      <style jsx>{`
        .translation-replace {
          align-items: center;
          background-color: ${theme.lightGrey};
          border-radius: 0 0 ${theme.cardBorderRadius} ${theme.cardBorderRadius};
          border-top: 2px solid white;
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          justify-content: space-between;
          padding: 8px 15px;
        }
        .translation-replace-detached {
          border-radius: ${theme.cardBorderRadius};
          margin-top: 15px;
        }
        .status {
          align-items: center;
          display: flex;
          gap: 5px;
        }
        .status-icon {
          color: ${theme.textColorLighter};
          font-size: 1.2rem;
        }
        .status-label {
          color: ${theme.textColor};
          font-size: 1.3rem;
        }
        .actions {
          display: flex;
          gap: 10px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockTranslationReplace;
